import { Box, Stack, StackProps, Tooltip, useTheme } from '@mui/material';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import { FractionDigitsFormattedNumber, PoolActionLabel, PoolRow } from '@synergy/components-next';
import { LeafIcon } from '@synergy/theme-next';
import { PoolMath } from '@synergy/utils';
import { ReactElement } from 'react';

import { ShoppingCartArticle } from '../../../../models/shopping-cart';

export interface SupplierTableRowProps extends StackProps {
    article: ShoppingCartArticle;
    netPrice: number;
}

export const ArticlePrice = (props: SupplierTableRowProps): ReactElement => {
    const theme = useTheme();
    const { article, netPrice, ...stackProps } = props;

    return (
        <Stack justifyContent="flex-end" typography="body2" width={129} marginLeft="auto" {...stackProps}>
            <Box
                color={article.specialPrice ? theme.palette.error.dark : theme.palette.common.black}
                typography="body1"
            >
                <FractionDigitsFormattedNumber value={netPrice} currency={article.currency} />
            </Box>
            {article.specialPrice && (
                <PoolRow justifyContent="flex-end">
                    <Box sx={{ textDecoration: 'line-through' }}>
                        <FractionDigitsFormattedNumber value={article.mainPrice} currency={article.currency} />
                    </Box>
                    <Box color={theme.palette.error.dark} marginLeft={0.5}>
                        <FormattedNumber
                            value={-PoolMath.getPercentDifference(article.mainPrice, article.specialPrice.price)}
                            style="percent"
                        />
                    </Box>
                </PoolRow>
            )}
            {(article.isBestPrice || article.isEcoProduct) && (
                <Stack flexDirection="row-reverse" gap={0.5} sx={{ my: 0.5, alignItems: 'flex-end' }}>
                    {article.isBestPrice && (
                        <PoolActionLabel variant="small" color="secondary" hasSkew={false}>
                            <FormattedMessage id="app.filter.bestprice" />
                        </PoolActionLabel>
                    )}
                    {article.isEcoProduct && (
                        <Tooltip title={<FormattedMessage id="app.tooltip.ecoProduct" />}>
                            <Stack>
                                <PoolActionLabel variant="small" color="success" hasSkew={false}>
                                    <LeafIcon sx={{ fontSize: 16 }} />
                                </PoolActionLabel>
                            </Stack>
                        </Tooltip>
                    )}
                </Stack>
            )}

            {article.contentUnit && (
                <Box color="grey.700">
                    &nbsp;(
                    <FractionDigitsFormattedNumber
                        value={(article.specialPrice?.price ?? article.mainPrice) / (article.contentQuantity ?? 1)}
                        currency={article.currency}
                    />
                    &nbsp;/&nbsp;
                    {article.contentUnit})
                </Box>
            )}
        </Stack>
    );
};
